import React from "react";
import {withRouter} from "react-router-dom";
import LoginPage from "../LoginPage";
import {takeSigninResume} from "../MfaSetupPage";
import LoadingScreen from "../../common/loading/LoadingScreen";
import LoginThemeScope from "./LoginThemeScope";
import {getLoginTheme} from "./LoginThemes";

// LoginPage itself, untouched, plus two hooks for a theme:
// - renderAfterItem: its own markup after a signin item (e.g. a heading under the logo),
// - renderSignedInAccount: its own "continue with" box for an already signed-in account, placed
//   under the logo (via renderAfterItem) instead of above it; LoginPage still decides whether it shows.
// With a theme that has neither, the page renders as upstream.
// For every theme, it also finishes the sign-in that MFA setup interrupted (see takeSigninResume).
class ThemedLoginPageBase extends LoginPage.WrappedComponent {
  constructor(props) {
    super(props);
    this.state.resumingSignin = false;
    this.pendingSigninResume = takeSigninResume();
  }

  componentDidMount() {
    super.componentDidMount();
    this.resumeSignin();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    this.resumeSignin();

    // a failed sign-in shows its error on the page; a successful one is leaving it
    if (this.state.resumingSignin && prevState.loginLoading && !this.state.loginLoading && !this.isRedirecting) {
      this.setState({resumingSignin: false});
    }
  }

  // the user, signed in by the sign-in that answered "RequiredMfa", is back from MFA setup: sign
  // in to the application with that session, as "Continue with" would, instead of asking again
  resumeSignin() {
    const application = this.getApplicationObj();
    if (!this.pendingSigninResume || !application || this.props.account === undefined) {
      return;
    }
    this.pendingSigninResume = false;
    // LoginPage signs in by itself with auto or silent sign-in
    const isSigningInItself = application.enableAutoSignin || new URLSearchParams(this.props.location.search).has("silentSignin");
    if (this.props.account?.owner !== application.organization || isSigningInItself) {
      return;
    }
    this.setState({resumingSignin: true, loginLoading: true});
    this.login({application: application.name});
  }

  postCodeLoginAction(resp) {
    this.isRedirecting = true;
    super.postCodeLoginAction(resp);
  }

  renderSignedInBox() {
    const box = super.renderSignedInBox();
    const application = this.getApplicationObj();
    const renderSignedInAccount = getLoginTheme(application).renderSignedInAccount;
    if (!renderSignedInAccount) {
      return box;
    }

    // rendered before the signin items, so the Logo item can pick it up; without a visible logo it stays on top
    this.showSignedInAccount = box !== null;
    const isLogoVisible = application?.signinItems?.some(signinItem => signinItem.name === "Logo" && signinItem.visible);
    return this.showSignedInAccount && !isLogoVisible ? renderSignedInAccount(this) : null;
  }

  continueAsSignedInAccount() {
    this.setState({loginLoading: true});
    this.login({application: this.getApplicationObj().name});
  }

  // the MFA step (Setting.renderMfaAuthVerifyForm) has no classes of its own; a themed page marks it
  renderLoginPanel(application) {
    const panel = super.renderLoginPanel(application);
    const isMfaStep = this.state.getVerifyTotp !== undefined && !this.isOrganizationChoiceBoxVisible(application.orgChoiceMode);
    return isMfaStep && getLoginTheme(application).className ? <div className="login-mfa">{panel}</div> : panel;
  }

  renderFormItem(application, signinItem) {
    const item = super.renderFormItem(application, signinItem);
    const extra = item ? getLoginTheme(application).renderAfterItem?.(signinItem.name, this) : null;
    return extra ? <React.Fragment key={item.key}>{item}{extra}</React.Fragment> : item;
  }

  render() {
    return (
      <React.Fragment>
        {super.render()}
        {this.state.resumingSignin ? <LoadingScreen /> : null}
      </React.Fragment>
    );
  }
}

const ThemedLoginPageInner = withRouter(ThemedLoginPageBase);

// Drop-in replacement for LoginPage that applies the application's login theme
function ThemedLoginPage(props) {
  return (
    <LoginThemeScope application={props.application}>
      <ThemedLoginPageInner {...props} />
    </LoginThemeScope>
  );
}

export default ThemedLoginPage;
